/**
 * ZineLead — the Prompt-Brush 2.0 Zine 1, given the top of the shop.
 *
 * The zine is the only thing in the shop that *is* Prompt-Brush rather than
 * merch about it, so it gets a spread viewer instead of a square card: you can
 * flip through all sixteen pages before deciding, which is the whole argument
 * for buying it.
 *
 * Each photograph was shot on the same grey seamless but at slightly different
 * exposures, so the frame takes its background from the shot on screen and
 * cross-fades. Without that, every spread lands inside a visible rectangle.
 */

import { useCallback, useEffect, useState } from 'react';

const MONO = 'FK Grotesk Mono, monospace';
const DISPLAY = 'Delcan Mono, monospace';
const SERIF = 'Martina Plantijn, Georgia, serif';

// Bought at delcan.co, the one shop for everything.
export const ZINE_PAYMENT_LINK = 'https://delcan.co/shop/prompt-brush-20-zine?utm_source=prompt-brush.com&utm_medium=referral';

// Cover, eight spreads, back cover — the order you'd flip it. `bg` is the
// measured edge of each photograph.
const SPREADS = [
  { src: '/shop/zine/gray-00.webp', bg: '#D2CED6', label: 'Cover' },
  { src: '/shop/zine/gray-01.webp', bg: '#E5E5E9', label: 'Spread 1' },
  { src: '/shop/zine/gray-02.webp', bg: '#E4E3E8', label: 'Spread 2' },
  { src: '/shop/zine/gray-03.webp', bg: '#ECECF0', label: 'Spread 3' },
  { src: '/shop/zine/gray-04.webp', bg: '#ECECF0', label: 'Spread 4' },
  { src: '/shop/zine/gray-05.webp', bg: '#ECECF0', label: 'Spread 5' },
  { src: '/shop/zine/gray-06.webp', bg: '#ECECF0', label: 'Spread 6' },
  { src: '/shop/zine/gray-07.webp', bg: '#ECECF0', label: 'Spread 7' },
  { src: '/shop/zine/gray-08.webp', bg: '#E7E6EB', label: 'Spread 8' },
  { src: '/shop/zine/gray-09.webp', bg: '#DDDCE2', label: 'Spread 9' },
  { src: '/shop/zine/gray-10.webp', bg: '#DAD9DE', label: 'Spread 10' },
  { src: '/shop/zine/gray-11.webp', bg: '#E8E7EC', label: 'Back cover' },
];

export function ZineLead() {
  const [i, setI] = useState(0);
  const at = SPREADS[i];

  const go = useCallback((step: number) => {
    setI((prev) => (prev + step + SPREADS.length) % SPREADS.length);
  }, []);

  // Arrow keys, because flipping a zine with a mouse is nobody's instinct.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  // The next spread is fetched while you look at this one, so flipping is instant.
  useEffect(() => {
    const next = new Image();
    next.src = SPREADS[(i + 1) % SPREADS.length].src;
  }, [i]);

  const arrow =
    'absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-black/10 ' +
    'bg-white/70 backdrop-blur text-black/70 hover:text-black hover:bg-white transition-colors';

  return (
    <section className="mt-14">
      <div className="text-muted-foreground uppercase tracking-widest mb-4" style={{ fontSize: '9pt', fontFamily: MONO }}>
        New — Zine 1
      </div>

      {/* ===== The spread ===== */}
      <div
        className="relative rounded-lg overflow-hidden border border-border"
        style={{ backgroundColor: at.bg, transition: 'background-color 0.35s ease' }}
      >
        <img
          key={at.src}
          src={at.src}
          alt={`Prompt-Brush 2.0 Zine 1 — ${at.label}`}
          onClick={() => go(1)}
          draggable={false}
          className="w-full cursor-pointer select-none"
          style={{ aspectRatio: '16 / 9', objectFit: 'contain' }}
        />
        <button onClick={() => go(-1)} aria-label="Previous page" className={`${arrow} left-4`} style={{ fontFamily: MONO }}>
          ←
        </button>
        <button onClick={() => go(1)} aria-label="Next page" className={`${arrow} right-4`} style={{ fontFamily: MONO }}>
          →
        </button>
      </div>

      {/* ===== Page strip ===== */}
      <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
        {SPREADS.map((s, n) => (
          <button
            key={s.src}
            onClick={() => setI(n)}
            aria-label={s.label}
            aria-current={n === i}
            className="shrink-0 rounded overflow-hidden border transition-opacity"
            style={{
              width: 64,
              aspectRatio: '16 / 9',
              backgroundColor: s.bg,
              borderColor: n === i ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.12)',
              opacity: n === i ? 1 : 0.55,
            }}
          >
            <img src={s.src} alt="" className="w-full h-full" style={{ objectFit: 'contain' }} loading="lazy" />
          </button>
        ))}
      </div>

      {/* ===== The pitch ===== */}
      <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-xl">
          <h2 className="text-foreground text-2xl leading-tight" style={{ fontFamily: DISPLAY }}>
            Prompt-Brush 2.0 / Zine 1
          </h2>
          <p className="text-foreground leading-relaxed mt-3" style={{ fontFamily: SERIF, fontSize: '15pt' }}>
            Sixteen pages, newsprint. Prompts sent in by anyone, drawn by hand by someone else —
            Javier Jaén, Najeebah Al-Ghadban, Edmon de Haro, Ben Grandgenett and Santiago
            Carrasquilla printed next to anonymous handles, every drawing credited the same way.
          </p>
        </div>
        <div className="shrink-0">
          <a
            href={ZINE_PAYMENT_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center rounded-full px-8 py-3 bg-foreground text-background hover:opacity-80 transition-opacity"
            style={{ fontFamily: MONO, fontSize: '11pt' }}
          >
            Buy · $25
          </a>
          <div className="text-muted-foreground text-center mt-2" style={{ fontFamily: MONO, fontSize: '9pt' }}>
            + shipping
          </div>
        </div>
      </div>
    </section>
  );
}
